import { useCallback, useEffect, useState } from 'react';

const NOTES_KEY = 'trip-planner-review-notes-v1';
const CHANGE_EVENT = 'trip-planner-review-notes-changed';
export const RENDER_CHAT_KEY = 'trip-planner-render-chat-v1';

export function readReviewNotes(): Record<string, string> {
  const parsed: unknown = JSON.parse(localStorage.getItem(NOTES_KEY) || '{}');
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    Array.isArray(parsed) ||
    Object.entries(parsed).some(([id, note]) => !/^\d+$/.test(id) || typeof note !== 'string')
  ) {
    throw new Error('Saved notes could not be read.');
  }
  return parsed as Record<string, string>;
}

export function useChatReviewStorage() {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const reload = useCallback(() => {
    try {
      setNotes(readReviewNotes());
      setError('');
    } catch {
      setError('Local notes could not be read. Existing storage has not been changed.');
    }
  }, []);
  useEffect(() => {
    reload();
    window.addEventListener('storage', reload);
    window.addEventListener(CHANGE_EVENT, reload);
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener(CHANGE_EVENT, reload);
    };
  }, [reload]);
  const save = (chatId: string, note: string) => {
    try {
      const next = { ...readReviewNotes(), [chatId]: note };
      if (!note.trim()) {
        delete next[chatId];
      }
      localStorage.setItem(NOTES_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return true;
    } catch {
      setError('Could not save locally. Keep this page open and copy your unsaved note before leaving.');
      return false;
    }
  };
  const clear = () => {
    try {
      localStorage.removeItem(NOTES_KEY);
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return true;
    } catch {
      setError('Could not clear local notes.');
      return false;
    }
  };
  return { notes, error, save, clear };
}
